import { useState, useEffect } from 'react';
import type { UseSheetData } from '@/hooks/useSheetData';
import { DEFAULT_THRESHOLDS } from '@/config/constants';
import { CUSTOM_VARS, ALERTS_UPDATED_EVENT } from '@/lib/alertDefs';
import type { VarDef } from '@/lib/alertDefs';
import {
  Save, Zap, Activity, Gauge, Radio, Waves, Thermometer, Bell, BellOff, BellRing,
  MessageSquare, Smartphone, ChevronDown, Plus, X, Search,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useLanguage } from '@/hooks/useLanguage';

interface AlertsProps { data: UseSheetData }

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

type Channel = 'in_app' | 'sms' | 'whatsapp';
type Cadence = 'instant' | 'daily' | 'weekly';

interface LevelConfig {
  enabled:   boolean;
  threshold: number;
  channel:   Channel;
  cadence:   Cadence;
}

interface AlertRule {
  id:       string;
  kind:     string;
  label:    string;
  enabled:  boolean;
  warning:  LevelConfig;
  critical: LevelConfig;
  isCustom?: boolean;
}

/* -------------------------------------------------------------------------- */
/*  Built-in metric definitions                                                */
/* -------------------------------------------------------------------------- */

interface MetricDef {
  kind:        string;
  label:       string;
  icon:        React.ReactNode;
  description: string;
  unit:        string;
  step:        number;
  higherIsBad: boolean;
  defaultWarn: number;
  defaultCrit: number;
  category:    string;
}

const BUILTIN_DEFS: MetricDef[] = [
  {
    kind: 'neutralCurrent',   label: 'Neutral Current',
    icon: <Zap className="w-4 h-4" />,
    description: 'Fires when neutral conductor carries excess current — fire risk.',
    unit: 'A',  step: 0.5,  higherIsBad: true,
    defaultWarn: DEFAULT_THRESHOLDS.neutralCurrentA,
    defaultCrit: DEFAULT_THRESHOLDS.neutralCurrentA * 3,
    category: 'Electrical Safety',
  },
  {
    kind: 'overload',         label: 'Approaching Contracted Demand',
    icon: <Gauge className="w-4 h-4" />,
    description: 'Fires when load % nears or exceeds the contracted demand limit.',
    unit: '%',  step: 1,    higherIsBad: true,
    defaultWarn: DEFAULT_THRESHOLDS.loadPctMax,
    defaultCrit: 100,
    category: 'Load',
  },
  {
    kind: 'currentUnbalance', label: 'Current Imbalance',
    icon: <Activity className="w-4 h-4" />,
    description: 'Fires when phase current imbalance exceeds threshold.',
    unit: '%',  step: 1,    higherIsBad: true,
    defaultWarn: DEFAULT_THRESHOLDS.currentUnbalancePct,
    defaultCrit: DEFAULT_THRESHOLDS.currentUnbalancePct * 2,
    category: 'Electrical Safety',
  },
  {
    kind: 'lowPF',            label: 'Low Power Factor',
    icon: <Radio className="w-4 h-4" />,
    description: 'Fires when PF drops below threshold — attracts utility penalty.',
    unit: '',   step: 0.01, higherIsBad: false,
    defaultWarn: DEFAULT_THRESHOLDS.pfMin,
    defaultCrit: 0.85,
    category: 'Power Quality',
  },
  {
    kind: 'voltageUnbalance', label: 'Voltage Imbalance',
    icon: <Waves className="w-4 h-4" />,
    description: 'Fires when phase voltage imbalance exceeds threshold.',
    unit: '%',  step: 0.5,  higherIsBad: true,
    defaultWarn: DEFAULT_THRESHOLDS.voltageUnbalancePct,
    defaultCrit: DEFAULT_THRESHOLDS.voltageUnbalancePct * 2,
    category: 'Power Quality',
  },
  {
    kind: 'highVoltageTHD', label: 'High Voltage Harmonic Distortion',
    icon: <Thermometer className="w-4 h-4" />,
    description: 'Fires when voltage THD exceeds safe levels — can damage capacitors, transformers, and sensitive equipment.',
    unit: '%',  step: 0.5,  higherIsBad: true,
    defaultWarn: 5,   // >5% voltage THD → Warning
    defaultCrit: 8,   // >8% voltage THD → Critical (IEEE 519 limit)
    category: 'Power Quality',
  },
  {
    kind: 'highCurrentTHD', label: 'High Current Harmonic Distortion',
    icon: <Thermometer className="w-4 h-4" />,
    description: 'Fires when current THD exceeds safe levels — indicates non-linear loads (VFDs, UPS, drives) causing transformer stress.',
    unit: '%',  step: 1,    higherIsBad: true,
    defaultWarn: 10,  // >10% current THD → Warning
    defaultCrit: 20,  // >20% current THD → Critical
    category: 'Power Quality',
  },
  {
    kind: 'highVoltageKFactor', label: 'High Voltage K-Factor',
    icon: <Waves className="w-4 h-4" />,
    description: 'Fires when voltage K-Factor exceeds transformer derating limits — K>4 requires K-rated transformer.',
    unit: '',   step: 0.5,  higherIsBad: true,
    defaultWarn: 4,   // K>4 → requires K-4 rated transformer
    defaultCrit: 13,  // K>13 → requires K-13 rated transformer
    category: 'Power Quality',
  },
  {
    kind: 'highCurrentKFactor', label: 'High Current K-Factor',
    icon: <Waves className="w-4 h-4" />,
    description: 'Fires when current K-Factor indicates severe harmonic loading — transformer must be derated or replaced with K-rated unit.',
    unit: '',   step: 0.5,  higherIsBad: true,
    defaultWarn: 4,   // K>4 → requires K-4 rated transformer
    defaultCrit: 13,  // K>13 → requires K-13 rated transformer
    category: 'Power Quality',
  },
];

/* -------------------------------------------------------------------------- */
/*  Storage & seed                                                             */
/* -------------------------------------------------------------------------- */

const STORAGE_KEY = 'cortex.alert.rules.v4';

function mkLevel(threshold: number, channel: Channel = 'in_app'): LevelConfig {
  return { enabled: true, threshold, channel, cadence: 'instant' };
}

function seedRules(): AlertRule[] {
  return BUILTIN_DEFS.map((m, i) => ({
    id: `r${i}`, kind: m.kind, label: m.label, enabled: true,
    warning:  mkLevel(m.defaultWarn),
    critical: mkLevel(m.defaultCrit, 'whatsapp'),
  }));
}

/* -------------------------------------------------------------------------- */
/*  Notification options                                                       */
/* -------------------------------------------------------------------------- */

const CHANNELS: { value: Channel; label: string; icon: React.ReactNode }[] = [
  { value: 'in_app',   label: 'In-app',   icon: <Bell className="w-3.5 h-3.5" /> },
  { value: 'sms',      label: 'SMS',      icon: <Smartphone className="w-3.5 h-3.5" /> },
  { value: 'whatsapp', label: 'WhatsApp', icon: <MessageSquare className="w-3.5 h-3.5" /> },
];

const CADENCES: { value: Cadence; label: string }[] = [
  { value: 'instant', label: 'Instant' },
  { value: 'daily',   label: 'Daily' },
  { value: 'weekly',  label: 'Weekly' },
];

/* -------------------------------------------------------------------------- */
/*  Helper: resolve step/higherIsBad for any rule kind                        */
/* -------------------------------------------------------------------------- */

function resolveKindMeta(kind: string): { step: number; higherIsBad: boolean } {
  const builtin = BUILTIN_DEFS.find(d => d.kind === kind);
  if (builtin) return { step: builtin.step, higherIsBad: builtin.higherIsBad };
  if (kind.startsWith('custom_')) {
    const key = kind.replace('custom_', '');
    const v   = CUSTOM_VARS.find(x => x.key === key);
    if (v) return { step: v.step, higherIsBad: v.higherIsBad };
  }
  return { step: 0.5, higherIsBad: true };
}

/* -------------------------------------------------------------------------- */
/*  Clash-guarded threshold patcher                                           */
/* -------------------------------------------------------------------------- */

/**
 * Returns the new threshold, clamped so that warning and critical never clash.
 * For higherIsBad metrics (current, load %): warn < crit.
 * For lowerIsBad metrics (PF):               warn > crit.
 */
function clampThreshold(
  level:       'warning' | 'critical',
  newValue:    number,
  otherValue:  number,
  step:        number,
  higherIsBad: boolean,
): number {
  if (higherIsBad) {
    if (level === 'warning') {
      // warn must stay at least one step below crit
      const max = parseFloat((otherValue - step).toFixed(3));
      return Math.min(newValue, max);
    } else {
      // crit must stay at least one step above warn
      const min = parseFloat((otherValue + step).toFixed(3));
      return Math.max(newValue, min);
    }
  } else {
    // lower is worse (PF): warn > crit
    if (level === 'warning') {
      const min = parseFloat((otherValue + step).toFixed(3));
      return Math.max(newValue, min);
    } else {
      const max = parseFloat((otherValue - step).toFixed(3));
      return Math.min(newValue, max);
    }
  }
}

/* -------------------------------------------------------------------------- */
/*  Main component                                                             */
/* -------------------------------------------------------------------------- */

export function Alerts({ data: _ }: AlertsProps) {
  const { t } = useLanguage();
  const [rules, setRules] = useState<AlertRule[]>(() => {
    // Keys of removed custom vars — filter out any saved rules for these
    const REMOVED_KEYS = new Set(['vlnAvg','vllAvg','vR','vY','vB','frequencyHz']);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as AlertRule[];
        // Remove deleted-metric rules that were saved before this update
        const filtered = parsed.filter(r => {
          if (!r.isCustom) {
            // Remove old merged highTHD builtin — replaced by highVoltageTHD + highCurrentTHD
            if (r.kind === 'highTHD') return false;
            return true;
          }
          const key = r.kind.replace('custom_', '');
          return !REMOVED_KEYS.has(key);
        });
        const kinds  = new Set(filtered.map(r => r.kind));
        const added  = BUILTIN_DEFS
          .filter(m => !kinds.has(m.kind))
          .map(m => ({
            id: `r_new_${m.kind}`, kind: m.kind, label: m.label, enabled: true,
            warning:  mkLevel(m.defaultWarn),
            critical: mkLevel(m.defaultCrit, 'whatsapp'),
          }));
        return [...filtered, ...added];
      }
      return seedRules();
    } catch {
      return seedRules();
    }
  });

  const [showPicker,   setShowPicker]   = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const [savedAt, setSavedAt] = useState<Date | null>(null);

  /**
   * Task 3 fix: auto-persist every rules change to localStorage and
   * dispatch ALERTS_UPDATED_EVENT so AnomalySection picks it up in the
   * same browser tab without waiting for a focus/storage event.
   */
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rules));
      window.dispatchEvent(new CustomEvent(ALERTS_UPDATED_EVENT));
    } catch { /* noop — quota or private-mode */ }
  }, [rules]);

  const saveRules = () => {
    // Auto-save already persisted; this just shows the confirmation stamp.
    setSavedAt(new Date());
  };

  /* Patch a top-level rule field */
  const patchRule = (id: string, patch: Partial<AlertRule>) =>
    setRules(xs => xs.map(r => r.id === id ? { ...r, ...patch } : r));

  /* Patch a level field, applying clash-guard when threshold changes */
  const patchLevel = (id: string, level: 'warning' | 'critical', patch: Partial<LevelConfig>) =>
    setRules(xs => xs.map(r => {
      if (r.id !== id) return r;
      const updated: LevelConfig = { ...r[level], ...patch };

      if (
        'threshold' in patch &&
        r.warning.enabled &&
        r.critical.enabled
      ) {
        const { step, higherIsBad } = resolveKindMeta(r.kind);
        const otherThreshold = level === 'warning'
          ? r.critical.threshold
          : r.warning.threshold;
        updated.threshold = clampThreshold(level, updated.threshold, otherThreshold, step, higherIsBad);
      }

      return { ...r, [level]: updated };
    }));

  /* Remove a custom rule */
  const removeRule = (id: string) => setRules(xs => xs.filter(r => r.id !== id));

  /* Add a custom variable as a new rule */
  const addCustom = (v: VarDef) => {
    if (rules.some(r => r.kind === `custom_${v.key}`)) return;
    setRules(xs => [...xs, {
      id:       `c_${Date.now()}`,
      kind:     `custom_${v.key}`,
      label:    v.label,
      enabled:  true,
      isCustom: true,
      warning:  mkLevel(v.defaultWarn),
      critical: mkLevel(v.defaultCrit, 'whatsapp'),
    }]);
    setShowPicker(false);
    setPickerSearch('');
  };

  const activeCount = rules.filter(r => r.enabled).length;

  const existingCustomKinds = new Set(
    rules.filter(r => r.isCustom).map(r => r.kind.replace('custom_', ''))
  );

  const filteredVars = CUSTOM_VARS.filter(v =>
    !existingCustomKinds.has(v.key) && (
      v.label.toLowerCase().includes(pickerSearch.toLowerCase()) ||
      v.category.toLowerCase().includes(pickerSearch.toLowerCase()) ||
      v.description.toLowerCase().includes(pickerSearch.toLowerCase())
    )
  );

  const grouped = filteredVars.reduce<Record<string, VarDef[]>>((acc, v) => {
    (acc[v.category] ??= []).push(v);
    return acc;
  }, {});

  return (
    <div className="space-y-3 pt-3 animate-fade-in pb-6">

      {/* Header */}
      <div className="px-1 flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-xl font-semibold text-slate-900">{t("Alert Rules")}</h1>
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 text-white text-[10px] font-bold">
              {activeCount}
            </span>
          )}
        </div>

      </div>

      {/* Rule cards — builtins always in BUILTIN_DEFS order, custom rules appended after */}
      {[
        // 1. All builtin rules in canonical BUILTIN_DEFS order
        ...BUILTIN_DEFS.map(def => rules.find(r => r.kind === def.kind)).filter((r): r is AlertRule => r != null),
        // 2. All custom rules in the order they were added
        ...rules.filter(r => r.isCustom),
      ].map(rule => {
        const builtinDef = BUILTIN_DEFS.find(d => d.kind === rule.kind);
        const customKey  = rule.isCustom ? rule.kind.replace('custom_', '') : null;
        const customVar  = customKey ? CUSTOM_VARS.find(v => v.key === customKey) : null;

        const def: MetricDef | null = builtinDef ?? (customVar ? {
          kind:        rule.kind,
          label:       rule.label,
          icon:        <Activity className="w-4 h-4" />,
          description: customVar.description,
          unit:        customVar.unit,
          step:        customVar.step,
          higherIsBad: customVar.higherIsBad,
          defaultWarn: customVar.defaultWarn,
          defaultCrit: customVar.defaultCrit,
          category:    customVar.category,
        } : null);

        if (!def) return null;

        return (
          <RuleCard
            key={rule.id}
            def={def}
            rule={rule}
            isCustom={!!rule.isCustom}
            onToggleMaster={() => patchRule(rule.id, { enabled: !rule.enabled })}
            onToggleLevel={lv  => patchLevel(rule.id, lv, { enabled: !rule[lv].enabled })}
            onThreshold={(lv, v)  => patchLevel(rule.id, lv, { threshold: v })}
            onChannel={(lv, ch)   => patchLevel(rule.id, lv, { channel: ch })}
            onCadence={(lv, cd)   => patchLevel(rule.id, lv, { cadence: cd })}
            onRemove={() => removeRule(rule.id)}
          />
        );
      })}

      {/* Add custom variable */}
      <button
        onClick={() => setShowPicker(v => !v)}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-500 transition-colors text-sm font-semibold"
      >
        <Plus className="w-4 h-4" />
        {t("Add custom variable alert")}
      </button>

      {/* Variable picker */}
      {showPicker && (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="px-4 pt-4 pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-800">{t("Choose a variable")}</p>
              <button
                onClick={() => { setShowPicker(false); setPickerSearch(''); }}
                className="text-slate-400 hover:text-slate-600 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={pickerSearch}
                onChange={e => setPickerSearch(e.target.value)}
                placeholder={t("Search variables…")}
                className="w-full pl-8 pr-3 h-8 text-xs rounded-xl bg-slate-100 border-0 outline-none focus:ring-2 focus:ring-slate-200 text-slate-700 font-medium"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
            {Object.entries(grouped).length === 0 && (
              <p className="px-4 py-4 text-xs text-slate-400 text-center">{t("No variables match your search.")}</p>
            )}
            {Object.entries(grouped).map(([cat, vars]) => (
              <div key={cat}>
                <p className="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50">
                  {t(cat)}
                </p>
                {vars.map(v => (
                  <button
                    key={v.key}
                    onClick={() => addCustom(v)}
                    className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 transition text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700">
                        {t(v.label)}
                        <span className="ml-1.5 text-[10px] font-medium text-slate-400">{v.unit}</span>
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{t(v.description)}</p>
                    </div>
                    <span className="text-[11px] text-slate-300 shrink-0 mt-0.5">→</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save button — bottom */}
      <button
        onClick={saveRules}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-600 text-white text-sm font-bold shadow-sm hover:bg-brand-700 active:scale-95 transition-all"
      >
        <Save className="w-4 h-4" />
        <span>{savedAt ? t('✓ Rules Saved Successfully') : t('Save Alert Rules')}</span>
      </button>

      <p className="text-[11px] text-slate-400 px-1 leading-relaxed">
        {t("Rules saved on this device. Production build routes notifications via SMS and WhatsApp.")}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  RuleCard                                                                   */
/* -------------------------------------------------------------------------- */

function RuleCard({
  def, rule, isCustom,
  onToggleMaster, onToggleLevel, onThreshold, onChannel, onCadence, onRemove,
}: {
  def:            MetricDef;
  rule:           AlertRule;
  isCustom:       boolean;
  onToggleMaster: () => void;
  onToggleLevel:  (level: 'warning' | 'critical') => void;
  onThreshold:    (level: 'warning' | 'critical', v: number) => void;
  onChannel:      (level: 'warning' | 'critical', ch: Channel) => void;
  onCadence:      (level: 'warning' | 'critical', cd: Cadence) => void;
  onRemove:       () => void;
}) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);

  const wActive = rule.enabled && rule.warning.enabled;
  const cActive = rule.enabled && rule.critical.enabled;

  return (
    <div className={cn(
      'rounded-2xl border overflow-hidden transition-all duration-150',
      rule.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50',
    )}>

      {/* Header — click to expand/collapse */}
      <button
        className="w-full px-4 pt-4 pb-3 flex items-start gap-3 text-left"
        onClick={() => setExpanded(v => !v)}
      >
        <div className={cn(
          'w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5',
          rule.enabled ? 'bg-slate-100 text-slate-600' : 'bg-slate-100 text-slate-300',
        )}>
          {def.icon}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className={cn('text-sm font-semibold', rule.enabled ? 'text-slate-800' : 'text-slate-400')}>
              {t(def.label)}
            </p>
            {isCustom && (
              <span className="text-[9px] font-bold bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded-md uppercase tracking-wide">
                {t("Custom")}
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-400 leading-snug">{t(def.description)}</p>

          {/* Collapsed chips */}
          {!expanded && rule.enabled && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              <LevelChip level="warning"  config={rule.warning}  unit={def.unit} />
              <LevelChip level="critical" config={rule.critical} unit={def.unit} />
            </div>
          )}
        </div>

        {/* Master toggle (custom only) + chevron */}
        <div
          className="flex items-center gap-2 shrink-0 mt-0.5"
          onClick={e => e.stopPropagation()}
        >
          {isCustom && (
            <button
              onClick={onToggleMaster}
              role="switch"
              aria-checked={rule.enabled}
              className={cn(
                'relative w-10 h-[22px] rounded-full transition-colors duration-200',
                rule.enabled ? 'bg-emerald-500' : 'bg-slate-200',
              )}
            >
              <span
                className="absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-all duration-200"
                style={{ left: rule.enabled ? 22 : 3 }}
              />
            </button>
          )}
          <ChevronDown className={cn(
            'w-4 h-4 text-slate-300 transition-transform duration-200',
            expanded && 'rotate-180',
          )} />
        </div>
      </button>

      {/* Expanded section */}
      {expanded && (
        <div className="border-t border-slate-100">
          <LevelPanel
            level="warning"
            config={rule.warning}
            otherConfig={rule.critical}
            def={def}
            masterEnabled={rule.enabled}
            onToggle={() => onToggleLevel('warning')}
            onThreshold={v  => onThreshold('warning', v)}
            onChannel={ch   => onChannel('warning', ch)}
            onCadence={cd   => onCadence('warning', cd)}
          />
          <LevelPanel
            level="critical"
            config={rule.critical}
            otherConfig={rule.warning}
            def={def}
            masterEnabled={rule.enabled}
            onToggle={() => onToggleLevel('critical')}
            onThreshold={v  => onThreshold('critical', v)}
            onChannel={ch   => onChannel('critical', ch)}
            onCadence={cd   => onCadence('critical', cd)}
          />
          {isCustom && (
            <div className="px-4 pb-3 pt-1">
              <button
                onClick={onRemove}
                className="text-[11px] font-semibold text-red-400 hover:text-red-600 transition flex items-center gap-1"
              >
                <X className="w-3 h-3" /> {t("Remove this rule")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-1.5">
        {rule.enabled
          ? <BellRing className="w-3 h-3 text-slate-400" />
          : <BellOff  className="w-3 h-3 text-slate-300" />}
        <span className="text-[11px] text-slate-400 truncate">
          {!rule.enabled
            ? t('Disabled — no notifications')
            : [
                wActive && `${t("Warn")} → ${t(CHANNELS.find(c => c.value === rule.warning.channel)?.label ?? '')}`,
                cActive && `${t("Crit")} → ${t(CHANNELS.find(c => c.value === rule.critical.channel)?.label ?? '')}`,
              ].filter(Boolean).join(' · ') || t('Both levels disabled')}
        </span>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  LevelChip — compact collapsed summary                                     */
/* -------------------------------------------------------------------------- */

function LevelChip({
  level, config, unit,
}: {
  level:  'warning' | 'critical';
  config: LevelConfig;
  unit:   string;
}) {
  const { t } = useLanguage();
  const isW  = level === 'warning';
  const isPF = unit === 'PF' || unit === '';
  const val  = isPF ? config.threshold.toFixed(2) : `${config.threshold}${unit}`;
  const ch   = CHANNELS.find(c => c.value === config.channel);

  if (!config.enabled) {
    return (
      <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
        {isW ? t('Warn') : t('Crit')} {t("off")}
      </span>
    );
  }

  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold',
      isW ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600',
    )}>
      {isW ? '▲' : '●'} {val} · {t(ch?.label ?? '')}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  LevelPanel — expanded threshold + notification config                     */
/* -------------------------------------------------------------------------- */

function LevelPanel({
  level, config, otherConfig, def, masterEnabled,
  onToggle, onThreshold, onChannel, onCadence,
}: {
  level:         'warning' | 'critical';
  config:        LevelConfig;
  otherConfig:   LevelConfig;
  def:           MetricDef;
  masterEnabled: boolean;
  onToggle:      () => void;
  onThreshold:   (v: number) => void;
  onChannel:     (ch: Channel) => void;
  onCadence:     (cd: Cadence) => void;
}) {
  const isW     = level === 'warning';
  const disabled = !masterEnabled || !config.enabled;

  const panelBg    = isW ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
  const accentText = isW ? 'text-amber-700'                : 'text-red-600';
  const dot        = isW ? 'bg-amber-400'                  : 'bg-red-500';
  const selBg      = isW
    ? 'border-amber-300 bg-amber-100 text-amber-700'
    : 'border-red-300 bg-red-100 text-red-700';

  // String input state — avoids leading-zero and type-coercion bugs
  const [inputStr, setInputStr] = useState(() => String(config.threshold));

  // Sync inputStr when threshold is changed externally (clash-clamp or +/- buttons)
  // useEffect avoids an infinite loop vs doing it inline
  useEffect(() => {
    setInputStr(String(config.threshold));
  }, [config.threshold]);

  const fmtDisplay = (): string => {
    if (def.unit === '' || def.unit === 'PF') return config.threshold.toFixed(2);
    return config.threshold % 1 === 0
      ? String(config.threshold)
      : config.threshold.toFixed(1);
  };

  const dec = () => {
    const next = Math.max(0, parseFloat((config.threshold - def.step).toFixed(3)));
    onThreshold(next);
  };

  const inc = () => {
    const next = parseFloat((config.threshold + def.step).toFixed(3));
    onThreshold(next);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputStr(e.target.value);
  };

  const handleInputBlur = () => {
    const parsed = parseFloat(inputStr);
    if (!isNaN(parsed) && parsed >= 0) {
      onThreshold(parsed);
    } else {
      setInputStr(String(config.threshold));
    }
  };

  // Compute constraint hint values
  const showHint = !disabled && otherConfig.enabled && masterEnabled;
  const { step: defStep, higherIsBad } = def;
  const boundaryValue = higherIsBad
    ? (level === 'warning' ? otherConfig.threshold - defStep : otherConfig.threshold + defStep)
    : (level === 'warning' ? otherConfig.threshold + defStep : otherConfig.threshold - defStep);
  const hintSign = (higherIsBad && level === 'warning') || (!higherIsBad && level === 'critical') ? '≤' : '≥';
  const hintDir  = level === 'warning'
    ? (higherIsBad ? 'below critical' : 'above critical')
    : (higherIsBad ? 'above warning'  : 'below warning');
  const hintLbl  = (def.unit === '' || def.unit === 'PF')
    ? boundaryValue.toFixed(2)
    : `${Number.isInteger(boundaryValue) ? boundaryValue : boundaryValue.toFixed(1)}${def.unit}`;

  const { t } = useLanguage();
  return (
    <div className={cn('mx-3 my-2.5 rounded-xl border p-3', panelBg)}>

      {/* Level header */}
      <div className="flex items-center gap-2 mb-3">
        <span className={cn('w-2 h-2 rounded-full shrink-0', dot)} />
        <span className={cn('text-xs font-bold uppercase tracking-wide', accentText)}>
          {isW ? t('Warning') : t('Critical')}
        </span>
        <span className="text-[11px] text-slate-500">
          {def.higherIsBad
            ? `${t("fires when >")} ${fmtDisplay()}${def.unit === 'PF' ? '' : def.unit}`
            : `${t("fires when <")} ${fmtDisplay()}${def.unit === 'PF' ? '' : def.unit}`}
        </span>
        {/* Level enable toggle */}
        <button
          onClick={onToggle}
          role="switch"
          aria-checked={config.enabled}
          className={cn(
            'ml-auto relative w-8 h-4 rounded-full transition-colors duration-200',
            config.enabled ? 'bg-emerald-500' : 'bg-slate-200',
          )}
        >
          <span
            className="absolute top-[2px] w-3 h-3 rounded-full bg-white shadow-sm transition-all duration-200"
            style={{ left: config.enabled ? 17 : 2 }}
          />
        </button>
      </div>

      {/* Threshold */}
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
        {t("Threshold")}
      </p>
      <div className="flex items-center gap-2 mb-1.5">
        <button
          onClick={dec}
          disabled={disabled}
          className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 font-bold text-base flex items-center justify-center disabled:opacity-30 active:scale-95 transition"
        >−</button>
        <div className="flex-1 flex items-center justify-center gap-1">
          <span className={cn('num text-xl font-bold', disabled ? 'text-slate-300' : accentText)}>
            {fmtDisplay()}
          </span>
          {def.unit && def.unit !== 'PF' && (
            <span className="text-sm font-semibold text-slate-400">{def.unit}</span>
          )}
        </div>
        <button
          onClick={inc}
          disabled={disabled}
          className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 font-bold text-base flex items-center justify-center disabled:opacity-30 active:scale-95 transition"
        >+</button>
        <input
          type="text"
          inputMode="decimal"
          value={inputStr}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
          className="num w-16 text-xs font-semibold rounded-lg bg-white border border-slate-200 px-1 h-8 outline-none focus:border-slate-400 text-center text-slate-700"
        />
      </div>

      {/* Constraint hint */}
      {showHint && (
        <p className="text-[10px] font-semibold text-slate-500 mb-3 flex items-center gap-1">
          <span className="opacity-60">⚠</span>
          {t("Must stay")} {t(hintDir)} — {t("limit")} {hintSign} {hintLbl}
        </p>
      )}

      {/* Channel */}
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
        {t("Notify via")}
      </p>
      <div className="flex gap-1.5 mb-3">
        {CHANNELS.map(ch => (
          <button
            key={ch.value}
            onClick={() => { if (!disabled) onChannel(ch.value); }}
            disabled={disabled}
            className={cn(
              'flex-1 flex flex-col items-center gap-0.5 py-2 rounded-xl border transition',
              config.channel === ch.value && !disabled ? selBg : 'border-slate-200 bg-white text-slate-400',
              disabled && 'opacity-30',
            )}
          >
            {ch.icon}
            <span className="text-[9px] font-semibold">{t(ch.label)}</span>
          </button>
        ))}
      </div>

      {/* Cadence */}
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
        {t("Frequency")}
      </p>
      <div className="flex gap-1.5">
        {CADENCES.map(cd => (
          <button
            key={cd.value}
            onClick={() => { if (!disabled) onCadence(cd.value); }}
            disabled={disabled}
            className={cn(
              'flex-1 py-1.5 rounded-lg border text-[10px] font-semibold transition',
              config.cadence === cd.value && !disabled
                ? 'border-slate-700 bg-slate-800 text-white'
                : 'border-slate-200 bg-white text-slate-400',
              disabled && 'opacity-30',
            )}
          >
            {t(cd.label)}
          </button>
        ))}
      </div>
    </div>
  );
}